import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Plus, Fuel, Gauge, Receipt, Camera, X, CheckCircle, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api';
import Webcam from 'react-webcam';
import Tesseract from 'tesseract.js';

const WorkerVehicleView = () => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state.auth);
  const [vehicles, setVehicles] = useState([]);
  const [selectedVehicleId, setSelectedVehicleId] = useState(null);
  const [fuelRecords, setFuelRecords] = useState([]);
  const [kilometerRecords, setKilometerRecords] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showFuelForm, setShowFuelForm] = useState(false);
  const [showKmForm, setShowKmForm] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const [showFuelCamera, setShowFuelCamera] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [fuelCameraError, setFuelCameraError] = useState(null);
  const webcamRef = useRef(null);
  const fuelWebcamRef = useRef(null);
  const [tesseractWorker, setTesseractWorker] = useState(null);
  
  const [fuelFormData, setFuelFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    amount: '',
    price: ''
  });
  
  const [kmFormData, setKmFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    time: new Date().toTimeString().slice(0, 5), // HH:mm format
    type: 'start', // 'start', 'update', or 'end'
    kilometers: '',
    notes: ''
  });

  // Initialize Tesseract worker
  useEffect(() => {
    const initTesseract = async () => {
      try {
        const worker = await Tesseract.createWorker('eng', 1, {
          logger: m => {
            if (m.status === 'recognizing text') {
              console.log(`OCR Progress: ${Math.round(m.progress * 100)}%`);
            }
          }
        });
        setTesseractWorker(worker);
      } catch (error) {
        console.error('Error initializing Tesseract:', error);
        toast.error('Greška pri inicijalizaciji OCR servisa');
      }
    };
    initTesseract();

    return () => {
      if (tesseractWorker) {
        tesseractWorker.terminate();
      }
    };
  }, []);

  useEffect(() => {
    fetchVehicleData();
  }, []);

  const fetchVehicleData = async () => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem('profile') || '{}')?.token;
      const workerId = user?.result?._id || JSON.parse(localStorage.getItem('profile') || '{}')?.result?._id;
      
      // Fetch vehicles assigned to this worker
      const vehicleResponse = await axios.get(`${API_BASE_URL}/api/vehicles/worker/${workerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const fetchedVehicles = vehicleResponse.data.vehicles || [];
      setVehicles(fetchedVehicles);

      // If vehicles exist and no vehicle is selected, select the first one
      if (fetchedVehicles.length > 0) {
        const vehicleToSelect = selectedVehicleId 
          ? fetchedVehicles.find(v => v._id === selectedVehicleId) || fetchedVehicles[0]
          : fetchedVehicles[0];
        
        setSelectedVehicleId(vehicleToSelect._id);
        await fetchVehicleRecords(vehicleToSelect._id, token);
      } else {
        setSelectedVehicleId(null);
        setFuelRecords([]);
        setKilometerRecords([]);
      }
    } catch (error) {
      console.error('Error fetching vehicle data:', error);
      if (error.response?.status !== 404) {
        toast.error('Greška pri učitavanju podataka o vozilu');
      }
      setVehicles([]);
      setSelectedVehicleId(null);
    } finally {
      setLoading(false);
    }
  };

  const fetchVehicleRecords = async (vehicleId, token) => {
    try {
      // Fetch fuel records
      const fuelResponse = await axios.get(
        `${API_BASE_URL}/api/vehicles/${vehicleId}/fuel`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFuelRecords(fuelResponse.data.records || []);

      // Fetch kilometer records
      const kmResponse = await axios.get(
        `${API_BASE_URL}/api/vehicles/${vehicleId}/kilometers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setKilometerRecords(kmResponse.data.records || []);
    } catch (error) {
      console.error('Error fetching vehicle records:', error);
    }
  };

  const handleVehicleChange = async (vehicleId) => {
    setSelectedVehicleId(vehicleId);
    const token = JSON.parse(localStorage.getItem('profile') || '{}')?.token;
    await fetchVehicleRecords(vehicleId, token);
  };

  const handleFuelSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = JSON.parse(localStorage.getItem('profile') || '{}')?.token;
      // Don't send image - only send extracted data
      const formDataToSend = {
        date: fuelFormData.date,
        amount: fuelFormData.amount,
        price: fuelFormData.price
      };

      await axios.post(
        `${API_BASE_URL}/api/vehicles/${selectedVehicleId}/fuel`,
        formDataToSend,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast.success('Račun za gorivo dodat');
      setShowFuelForm(false);
      setShowFuelCamera(false);
                    setFuelFormData({ date: new Date().toISOString().split('T')[0], amount: '', price: '' });
      fetchVehicleData();
    } catch (error) {
      console.error('Error adding fuel record:', error);
      toast.error(error.response?.data?.message || 'Greška pri dodavanju računa');
    }
  };

  // Removed capturePhoto - we only use OCR scanning, images are not saved

  const scanFuelReceipt = useCallback(async () => {
    if (!tesseractWorker) {
      toast.error('OCR servis nije spreman');
      return;
    }

    setIsScanning(true);
    try {
      const imageSrc = fuelWebcamRef.current?.getScreenshot();
      if (!imageSrc) {
        toast.error('Nije moguće snimiti fotografiju');
        setIsScanning(false);
        return;
      }

      // Convert base64 to blob for OCR
      const response = await fetch(imageSrc);
      const blob = await response.blob();

      // Perform OCR
      const { data: { text } } = await tesseractWorker.recognize(blob);
      
      console.log('OCR Text from receipt:', text);
      
      // Extract numbers from text - look for amount (liters) and price
      // Common patterns: "15.50 L", "15,50 L", "15.50L", "Cena: 1200", "1200 RSD", etc.
      const numbers = text.match(/[\d,\.]+/g) || [];
      
      // Try to find amount (usually smaller number, might have decimal)
      // and price (usually larger number, might be in RSD)
      const decimalNumbers = numbers
        .map(num => parseFloat(num.replace(',', '.')))
        .filter(num => !isNaN(num) && num > 0)
        .sort((a, b) => a - b);

      if (decimalNumbers.length >= 2) {
        // Smallest number is likely amount (liters), largest is likely price
        const amount = decimalNumbers[0] < 100 ? decimalNumbers[0] : null;
        const price = decimalNumbers[decimalNumbers.length - 1] > 100 ? decimalNumbers[decimalNumbers.length - 1] : null;
        
        if (amount && price) {
          setFuelFormData({ 
            ...fuelFormData, 
            amount: amount.toString(),
            price: Math.round(price).toString()
          });
          setShowFuelCamera(false);
          toast.success(`Pronađeno: ${amount}L, ${Math.round(price)} RSD`);
        } else if (amount) {
          setFuelFormData({ 
            ...fuelFormData, 
            amount: amount.toString()
          });
          setShowFuelCamera(false);
          toast.success(`Pronađena količina: ${amount}L`);
        } else if (price) {
          setFuelFormData({ 
            ...fuelFormData, 
            price: Math.round(price).toString()
          });
          setShowFuelCamera(false);
          toast.success(`Pronađena cena: ${Math.round(price)} RSD`);
        } else {
          toast.warning('Nije moguće prepoznati podatke sa računa. Pokušajte ponovo ili unesite ručno.');
        }
      } else {
        toast.warning('Nije moguće prepoznati podatke sa računa. Pokušajte ponovo ili unesite ručno.');
      }
    } catch (error) {
      console.error('Error scanning receipt:', error);
      toast.error('Greška pri skeniranju računa');
    } finally {
      setIsScanning(false);
    }
  }, [tesseractWorker, fuelFormData]);

  const scanOdometer = useCallback(async () => {
    if (!tesseractWorker) {
      toast.error('OCR servis nije spreman');
      return;
    }

    setIsScanning(true);
    try {
      const imageSrc = webcamRef.current?.getScreenshot();
      if (!imageSrc) {
        toast.error('Nije moguće snimiti fotografiju');
        setIsScanning(false);
        return;
      }

      // Convert base64 to blob for OCR
      const response = await fetch(imageSrc);
      const blob = await response.blob();

      // Perform OCR
      const { data: { text } } = await tesseractWorker.recognize(blob);
      
      // Extract numbers from text (look for odometer reading - typically 5-7 digits)
      const numbers = text.match(/\d+/g) || [];
      // Find the longest number sequence (likely the odometer reading)
      const odometerReading = numbers.reduce((longest, num) => 
        num.length > longest.length ? num : longest, ''
      );

      if (odometerReading && odometerReading.length >= 4) {
        setKmFormData({ ...kmFormData, kilometers: odometerReading });
        setShowCamera(false);
        toast.success(`Pronađeno stanje kilometar sata: ${odometerReading} km`);
      } else {
        toast.warning('Nije moguće prepoznati kilometražu. Pokušajte ponovo ili unesite ručno.');
      }
    } catch (error) {
      console.error('Error scanning odometer:', error);
      toast.error('Greška pri skeniranju kilometar sata');
    } finally {
      setIsScanning(false);
    }
  }, [tesseractWorker, kmFormData]);

  const handleKmSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = JSON.parse(localStorage.getItem('profile') || '{}')?.token;
      // Don't send image - only send extracted data
      const formDataToSend = {
        date: kmFormData.date,
        time: kmFormData.time,
        type: kmFormData.type,
        kilometers: kmFormData.kilometers,
        notes: kmFormData.notes || ''
      };

      await axios.post(
        `${API_BASE_URL}/api/vehicles/${selectedVehicleId}/kilometers`,
        formDataToSend,
        { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } }
      );
      toast.success('Kilometraža dodata');
      setShowKmForm(false);
      setKmFormData({ 
        date: new Date().toISOString().split('T')[0], 
        time: new Date().toTimeString().slice(0, 5),
        type: 'start',
        kilometers: '', 
        notes: ''
      });
      fetchVehicleData();
    } catch (error) {
      console.error('Error adding kilometer record:', error);
      toast.error(error.response?.data?.message || 'Greška pri dodavanju kilometraže');
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'start': return 'Početno stanje';
      case 'update': return 'Ažurirano stanje';
      case 'end': return 'Završno stanje';
      default: return type;
    }
  };

  if (loading) {
    return <div className="p-6 text-center text-gray-400">Učitavanje...</div>;
  }

  const selectedVehicle = vehicles.find(v => v._id === selectedVehicleId);

  if (vehicles.length === 0) {
    return (
      <div className="p-6">
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <p className="text-gray-400">Nemate dodeljeno vozilo</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white mb-2">Moja vozila</h1>
        
        {/* Vehicle Selector */}
        {vehicles.length > 1 && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-300 mb-2">
              Izaberite vozilo:
            </label>
            <select
              value={selectedVehicleId || ''}
              onChange={(e) => handleVehicleChange(e.target.value)}
              className="w-full md:w-auto px-4 py-2 bg-gray-700 border border-gray-600 rounded-md text-white"
            >
              {vehicles.map(vehicle => (
                <option key={vehicle._id} value={vehicle._id}>
                  {vehicle.registrationNumber} - {vehicle.model}
                </option>
              ))}
            </select>
          </div>
        )}

        {selectedVehicle && (
          <div className="bg-gray-800 rounded-lg p-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <span className="text-gray-400">Registarska oznaka:</span>
                <p className="text-white font-semibold">{selectedVehicle.registrationNumber}</p>
              </div>
              <div>
                <span className="text-gray-400">Model:</span>
                <p className="text-white font-semibold">{selectedVehicle.model}</p>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Fuel Section */}
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Fuel className="h-5 w-5 text-green-400" />
              Sipano gorivo
            </h2>
            <button
              onClick={() => setShowFuelForm(!showFuelForm)}
              className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
            >
              <Plus className="h-4 w-4" />
              Dodaj račun
            </button>
          </div>

          {showFuelForm && (
            <form onSubmit={handleFuelSubmit} className="mb-4 space-y-3 bg-gray-700 p-4 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Datum</label>
                <input
                  type="date"
                  value={fuelFormData.date}
                  onChange={(e) => setFuelFormData({ ...fuelFormData, date: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Količina (L)</label>
                <input
                  type="number"
                  step="0.01"
                  value={fuelFormData.amount}
                  onChange={(e) => setFuelFormData({ ...fuelFormData, amount: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-1">Cena</label>
                <input
                  type="number"
                  step="0.01"
                  value={fuelFormData.price}
                  onChange={(e) => setFuelFormData({ ...fuelFormData, price: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white"
                  required
                />
              </div>
              {/* Mobile: Kamera u novom redu */}
              <div className="block md:hidden">
                <label className="block text-sm font-medium text-gray-300 mb-1">Skeniraj račun kamerom</label>
                <button
                  type="button"
                  onClick={() => {
                    setShowFuelCamera(true);
                    setFuelCameraError(null);
                  }}
                  className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-center gap-2"
                  title="Skeniraj račun i automatski izvuci podatke"
                >
                  <Camera className="h-4 w-4" />
                  Skeniraj račun
                </button>
              </div>
              
              {/* Desktop: Kamera dugme */}
              <div className="hidden md:block">
                <label className="block text-sm font-medium text-gray-300 mb-1">Skeniraj račun kamerom</label>
                <button
                  type="button"
                  onClick={() => {
                    setShowFuelCamera(true);
                    setFuelCameraError(null);
                  }}
                  className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-center gap-2"
                  title="Skeniraj račun i automatski izvuci podatke"
                >
                  <Camera className="h-4 w-4" />
                  Skeniraj račun
                </button>
              </div>
              <div className="flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                >
                  Sačuvaj
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowFuelForm(false);
                    setShowFuelCamera(false);
                    setFuelFormData({ date: new Date().toISOString().split('T')[0], amount: '', price: '' });
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
                >
                  Otkaži
                </button>
              </div>
            </form>
          )}

          <div className="space-y-2">
            {fuelRecords.length === 0 ? (
              <p className="text-gray-400 text-sm">Nema unetih računa</p>
            ) : (
              fuelRecords.map((record, index) => (
                <div key={index} className="bg-gray-700 rounded p-3 flex justify-between items-center">
                  <div>
                    <p className="text-white font-medium">{record.amount}L</p>
                    <p className="text-gray-400 text-sm">{new Date(record.date).toLocaleDateString()}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-white">{record.price} RSD</p>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Kilometers Section */}
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold text-white flex items-center gap-2">
              <Gauge className="h-5 w-5 text-yellow-400" />
              Predjeni kilometri
            </h2>
            <button
              onClick={() => {
                setShowKmForm(!showKmForm);
                setKmFormData({
                  date: new Date().toISOString().split('T')[0],
                  time: new Date().toTimeString().slice(0, 5),
                  type: 'start',
                  kilometers: '',
                  notes: ''
                });
              }}
              className="flex items-center gap-2 px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm"
            >
              <Plus className="h-4 w-4" />
              Dodaj
            </button>
          </div>

          {showKmForm && (
            <form onSubmit={handleKmSubmit} className="mb-4 space-y-3 bg-gray-700 p-4 rounded-lg">
              {/* Mobile: Datum i vreme u jednom redu, Desktop: odvojeno */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Datum *</label>
                  <input
                    type="date"
                    value={kmFormData.date}
                    onChange={(e) => setKmFormData({ ...kmFormData, date: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white"
                    lang="sr-RS"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">Vreme unosa *</label>
                  <input
                    type="time"
                    value={kmFormData.time}
                    onChange={(e) => setKmFormData({ ...kmFormData, time: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white"
                    required
                  />
                </div>
              </div>
              
              {/* Mobile: Tip unosa u novom redu */}
              <div className="block md:hidden">
                <label className="block text-sm font-medium text-gray-300 mb-1">Tip unosa *</label>
                <select
                  value={kmFormData.type}
                  onChange={(e) => setKmFormData({ ...kmFormData, type: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white"
                  required
                >
                  <option value="start">Početno stanje</option>
                  <option value="update">Ažurirano stanje</option>
                  <option value="end">Završno stanje</option>
                </select>
              </div>
              
              {/* Desktop: Tip unosa u grid-u sa vremenom */}
              <div className="hidden md:block">
                <label className="block text-sm font-medium text-gray-300 mb-1">Tip unosa *</label>
                <select
                  value={kmFormData.type}
                  onChange={(e) => setKmFormData({ ...kmFormData, type: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white"
                  required
                >
                  <option value="start">Početno stanje</option>
                  <option value="update">Ažurirano stanje</option>
                  <option value="end">Završno stanje</option>
                </select>
              </div>

              {/* Mobile: Input i dugme u novom redu */}
              <div className="block md:hidden">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Stanje kilometar sata (km) *
                </label>
                <input
                  type="number"
                  value={kmFormData.kilometers}
                  onChange={(e) => setKmFormData({ ...kmFormData, kilometers: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white mb-2"
                  placeholder="Unesite kilometražu"
                  required
                />
                <button
                  type="button"
                  onClick={() => {
                    setShowCamera(true);
                    setCameraError(null);
                  }}
                  className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center justify-center gap-2"
                  title="Skeniraj kilometar sat kamerom"
                >
                  <Camera className="h-4 w-4" />
                  Kamera
                </button>
              </div>
              
              {/* Desktop: Input i dugme u istom redu */}
              <div className="hidden md:block">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Stanje kilometar sata (km) *
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    value={kmFormData.kilometers}
                    onChange={(e) => setKmFormData({ ...kmFormData, kilometers: e.target.value })}
                    className="flex-1 px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white"
                    placeholder="Unesite kilometražu"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setShowCamera(true);
                      setCameraError(null);
                    }}
                    className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg flex items-center gap-2"
                    title="Skeniraj kilometar sat kamerom"
                  >
                    <Camera className="h-4 w-4" />
                    Kamera
                  </button>
                </div>
              </div>

              {/* Mobile: Napomene u novom redu */}
              <div className="block md:hidden">
                <label className="block text-sm font-medium text-gray-300 mb-1">Napomene</label>
                <textarea
                  value={kmFormData.notes}
                  onChange={(e) => setKmFormData({ ...kmFormData, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white mb-2"
                  rows="3"
                  placeholder="Dodatne napomene..."
                />
              </div>
              
              {/* Desktop: Napomene */}
              <div className="hidden md:block">
                <label className="block text-sm font-medium text-gray-300 mb-1">Napomene</label>
                <textarea
                  value={kmFormData.notes}
                  onChange={(e) => setKmFormData({ ...kmFormData, notes: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-600 border border-gray-500 rounded-md text-white"
                  rows="3"
                  placeholder="Dodatne napomene..."
                />
              </div>

              {/* Mobile: Dugmad u novom redu, jedno ispod drugog */}
              <div className="block md:hidden space-y-2">
                <button
                  type="submit"
                  className="w-full px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                >
                  Sačuvaj
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowKmForm(false);
                    setShowCamera(false);
                    setKmFormData({ 
                      date: new Date().toISOString().split('T')[0], 
                      time: new Date().toTimeString().slice(0, 5),
                      type: 'start',
                      kilometers: '', 
                      notes: '',
                      image: null
                    });
                  }}
                  className="w-full px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
                >
                  Otkaži
                </button>
              </div>
              
              {/* Desktop: Dugmad u istom redu */}
              <div className="hidden md:flex gap-2">
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg"
                >
                  Sačuvaj
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowKmForm(false);
                    setShowCamera(false);
                    setKmFormData({ 
                      date: new Date().toISOString().split('T')[0], 
                      time: new Date().toTimeString().slice(0, 5),
                      type: 'start',
                      kilometers: '', 
                      notes: '',
                      image: null
                    });
                  }}
                  className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
                >
                  Otkaži
                </button>
              </div>
            </form>
          )}

          {/* Fuel Camera Modal */}
          {showFuelCamera && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-white">Snimi račun za gorivo</h3>
                  <button
                    onClick={() => {
                      setShowFuelCamera(false);
                      setFuelCameraError(null);
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="mb-4 bg-gray-900 rounded-lg p-4">
                  <p className="text-sm text-gray-300 mb-2">
                    Postavite kameru na račun i kliknite "Skeniraj automatski" da se izvuku količina i cena
                  </p>
                  {fuelCameraError ? (
                    <div className="bg-red-900/50 border border-red-500 rounded p-3 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                      <p className="text-red-300 text-sm">{fuelCameraError}</p>
                    </div>
                  ) : (
                    <Webcam
                      audio={false}
                      ref={fuelWebcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        facingMode: 'environment', // Use back camera on mobile
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                      }}
                      onUserMediaError={(error) => {
                        setFuelCameraError('Greška pri pristupu kameri. Proverite dozvole.');
                        console.error('Camera error:', error);
                      }}
                      className="w-full rounded-lg"
                    />
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={scanFuelReceipt}
                    disabled={isScanning || !tesseractWorker || fuelCameraError}
                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center gap-2"
                  >
                    {isScanning ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Skeniranje...
                      </>
                    ) : (
                      <>
                        <Camera className="h-4 w-4" />
                        Skeniraj automatski
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowFuelCamera(false);
                      setFuelCameraError(null);
                    }}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
                  >
                    Zatvori
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Camera Modal for Odometer */}
          {showCamera && (
            <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
              <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-xl font-semibold text-white">Skeniraj kilometar sat</h3>
                  <button
                    onClick={() => {
                      setShowCamera(false);
                      setCameraError(null);
                    }}
                    className="text-gray-400 hover:text-white"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>
                
                <div className="mb-4 bg-gray-900 rounded-lg p-4">
                  <p className="text-sm text-gray-300 mb-2">
                    Postavite kameru na kilometar tablu u automobilu i kliknite "Skeniraj"
                  </p>
                  {cameraError ? (
                    <div className="bg-red-900/50 border border-red-500 rounded p-3 flex items-center gap-2">
                      <AlertCircle className="h-5 w-5 text-red-400" />
                      <p className="text-red-300 text-sm">{cameraError}</p>
                    </div>
                  ) : (
                    <Webcam
                      audio={false}
                      ref={webcamRef}
                      screenshotFormat="image/jpeg"
                      videoConstraints={{
                        facingMode: 'environment', // Use back camera on mobile
                        width: { ideal: 1280 },
                        height: { ideal: 720 }
                      }}
                      onUserMediaError={(error) => {
                        setCameraError('Greška pri pristupu kameri. Proverite dozvole.');
                        console.error('Camera error:', error);
                      }}
                      className="w-full rounded-lg"
                    />
                  )}
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={scanOdometer}
                    disabled={isScanning || !tesseractWorker || cameraError}
                    className="flex-1 px-4 py-2 bg-purple-600 hover:bg-purple-700 disabled:bg-gray-600 disabled:cursor-not-allowed text-white rounded-lg flex items-center justify-center gap-2"
                  >
                    {isScanning ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Skeniranje...
                      </>
                    ) : (
                      <>
                        <Camera className="h-4 w-4" />
                        Skeniraj automatski
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => {
                      setShowCamera(false);
                      setCameraError(null);
                    }}
                    className="px-4 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg"
                  >
                    Zatvori
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-2">
            {kilometerRecords.length === 0 ? (
              <p className="text-gray-400 text-sm">Nema unetih kilometara</p>
            ) : (
              kilometerRecords
                .sort((a, b) => {
                  const dateA = new Date(a.date);
                  const dateB = new Date(b.date);
                  if (dateA.getTime() !== dateB.getTime()) {
                    return dateB - dateA;
                  }
                  // If same date, sort by time
                  return (b.time || '').localeCompare(a.time || '');
                })
                .map((record, index) => (
                  <div key={index} className="bg-gray-700 rounded p-3">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-white font-medium">{record.kilometers} km</p>
                          <span className="px-2 py-1 bg-blue-600 text-white text-xs rounded">
                            {getTypeLabel(record.type)}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm">
                          {new Date(record.date).toLocaleDateString()} {record.time && `u ${record.time}`}
                        </p>
                      </div>
                    </div>
                    {record.notes && (
                      <p className="text-gray-400 text-sm mt-1">{record.notes}</p>
                    )}
                  </div>
                ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkerVehicleView;
